import { useCallback, useState } from 'react'

import { Box, Button, Divider, Group, Modal, Stack, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconFileExport } from '@tabler/icons-react'
import { useNotify, useTranslate } from 'react-admin'
import { ScamExportJobResponseApiDTO } from '../../codegen/generated/Api.schemas'
import { usePostApiScamExportExport } from '../../codegen/generated/scam-export'
import { SCAMExportsFilters } from '../../components/Exports/SCAM/SCAMExportsFilters'
import { SCAMExportsPresets } from '../../components/Exports/SCAM/SCAMExportsPresets'
import { SCAMExportsPreview } from '../../components/Exports/SCAM/SCAMExportsPreview'
import { SCAMExportsProgress } from '../../components/Exports/SCAM/SCAMExportsProgress'
import { LXDatePeriod } from '../../components/LXDatePeriod'
import { useExportsContext, useExportsDispatchContext } from '../../contexts/ExportsContext'

export const SCAMExports = () => {
  const t = useTranslate()
  const notify = useNotify()
  const context = useExportsContext()
  const { setSCAMPeriod } = useExportsDispatchContext()

  const [isModalOpened, { open: openModal, close: closeModal }] = useDisclosure(false)
  const [jobId, setJobId] = useState<ScamExportJobResponseApiDTO['id'] | undefined>()

  const exportMutation = usePostApiScamExportExport()

  const onPeriodUpdate = useCallback(
    (
      { month, year }: { month: string | undefined; year: string | undefined },
      periodType: 'start' | 'end',
    ) => {
      if (periodType === 'start') {
        setSCAMPeriod(p => ({
          ...p,
          startMonth: month ? parseInt(month) : undefined,
          startYear: year ? parseInt(year) : undefined,
        }))
      } else if (periodType === 'end') {
        setSCAMPeriod(p => ({
          ...p,
          ...{
            endMonth: month ? parseInt(month) : undefined,
            endYear: year ? parseInt(year) : undefined,
          },
        }))
      }
    },
    [setSCAMPeriod],
  )

  const onExport = useCallback(async () => {
    try {
      const { filters, period } = context.scam

      if (filters && period) {
        const res = await exportMutation.mutateAsync({
          data: { filters, period },
        })

        setJobId(res.body.job.id)
        openModal()
      }
    } catch (e: any) {
      console.error(`Error - export scam - ${e}`)
      notify(
        `${t('pages.exports.toast_exportError')} - ${
          e.response?.data?.message ? e.response.data.message : e
        }`,
        { type: 'error' },
      )
    }
  }, [context.scam, exportMutation.mutateAsync, t, notify, openModal])

  return (
    <Stack gap="xl" mt="lg">
      <Group align="flex-start" justify="space-between">
        <SCAMExportsPresets />
        <Group align="flex-start" justify="space-evenly" gap={0}>
          <LXDatePeriod
            month={context.scam.period.startMonth?.toString()}
            year={context.scam.period.startYear?.toString()}
            monthLabel={t('pages.exports.startPeriod')}
            yearLabel=" "
            onUpdate={(month, year) => onPeriodUpdate({ month, year }, 'start')}
            clearable
            searchable
          />
          <Divider orientation="vertical" mr="xl" />
          <LXDatePeriod
            month={context.scam.period.endMonth?.toString()}
            year={context.scam.period.endYear?.toString()}
            monthLabel={t('pages.exports.endPeriod')}
            yearLabel=" "
            onUpdate={(month, year) => onPeriodUpdate({ month, year }, 'end')}
            clearable
            searchable
          />
        </Group>
      </Group>

      <Box>
        <Title order={4}>{t('_generics.filters')}</Title>
        <Divider mt="xs" mb="xl" />
        <SCAMExportsFilters />
      </Box>

      <Box>
        <Group justify="space-between">
          <Title order={4}>{t('_generics.preview')}</Title>
          <Button
            size="compact-md"
            onClick={() => onExport()}
            leftSection={<IconFileExport size={14} />}
          >
            {t('_generics.export')}
          </Button>
        </Group>
        <Divider mt="xs" mb="xl" />
        <SCAMExportsPreview />
      </Box>

      <Modal
        title={<Title size="h3">{t('pages.exports.exportProgress')}</Title>}
        opened={isModalOpened}
        onClose={closeModal}
      >
        <SCAMExportsProgress jobId={jobId} onSuccess={closeModal} />
      </Modal>
    </Stack>
  )
}
